import React, { memo, useState } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { LockRounded } from '@mui/icons-material';
import { subjectHue } from './timetableUtils';

interface LessonCardProps {
  subjectId: string;
  title: string;
  subtitle?: string;
  meta?: string | null;
  locked?: boolean;
  compact?: boolean;
  /** Faded, e.g. while being dragged (the card also fades itself when it is the one dragged). */
  ghost?: boolean;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  /** Extra corner content, e.g. a count in the unplaced tray. */
  badge?: React.ReactNode;
}

/** A lesson on the timetable: subject colour, subject, and who or where. */
const LessonCard = memo<LessonCardProps>(({
  subjectId, title, subtitle, meta, locked, compact, ghost, draggable, onDragStart, onDragEnd, onClick, badge,
}) => {
  const theme = useTheme();
  // The card fades itself while dragged, so a drag redraws one card, not the board.
  const [dragging, setDragging] = useState(false);
  const dark = theme.palette.mode === 'dark';
  const hue = subjectHue(subjectId);
  const accent = `hsl(${Math.round(hue)}, ${dark ? '70%, 68%' : '62%, 46%'})`;

  return (
    <Box
      draggable={draggable}
      onDragStart={draggable ? (e) => { setDragging(true); onDragStart?.(e); } : undefined}
      onDragEnd={draggable ? () => { setDragging(false); onDragEnd?.(); } : undefined}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e as any); } } : undefined}
      sx={{
        position: 'relative',
        height: '100%',
        minWidth: 0,
        borderRadius: '8px',
        px: compact ? 0.75 : 1,
        py: compact ? 0.5 : 0.75,
        bgcolor: alpha(accent, dark ? 0.16 : 0.11),
        boxShadow: `inset 3px 0 0 ${accent}`,
        cursor: draggable ? 'grab' : onClick ? 'pointer' : 'default',
        opacity: ghost || dragging ? 0.35 : 1,
        outline: 'none',
        transition: 'box-shadow 120ms, transform 120ms, opacity 120ms',
        userSelect: 'none',
        '&:hover': (draggable || onClick) ? { boxShadow: `inset 3px 0 0 ${accent}, 0 2px 10px ${alpha(theme.palette.common.black, dark ? 0.4 : 0.1)}` } : undefined,
        '&:active': draggable ? { cursor: 'grabbing' } : undefined,
        '&:focus-visible': { boxShadow: `inset 3px 0 0 ${accent}, 0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)}` },
      }}
    >
      <Typography
        variant="body2"
        noWrap
        sx={{ fontWeight: 650, fontSize: compact ? '0.72rem' : '0.8rem', lineHeight: 1.3, pr: locked || badge ? 2 : 0 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" noWrap component="div" sx={{ color: 'text.secondary', fontSize: compact ? '0.66rem' : '0.72rem', lineHeight: 1.35 }}>
          {subtitle}
        </Typography>
      )}
      {meta && !compact && (
        <Typography variant="caption" noWrap component="div" sx={{ color: 'text.disabled', fontSize: '0.68rem', lineHeight: 1.3 }}>
          {meta}
        </Typography>
      )}
      {(locked || badge) && (
        <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
          {badge}
          {locked && <LockRounded sx={{ fontSize: 12 }} aria-label="Locked" />}
        </Box>
      )}
    </Box>
  );
});

LessonCard.displayName = 'LessonCard';

export default LessonCard;
